import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserPlus, Search, Upload, Inbox, Users, PhoneOff, BookOpen, MessageSquare, LineChart, TrendingUp, Check } from "lucide-react";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import api from "@/lib/api";
import VideoModal from "@/components/marketing/VideoModal";
import VideoTeaserButton from "@/components/marketing/VideoTeaserButton";
import { marketingVideos } from "@/config/marketingVideos";

const SHOP_BG =
  "https://static.prod-images.emergentagent.com/jobs/e16202cb-95d0-411c-902c-bd0d02e3145b/images/f5bc4a7fe6e1676168c6e0591718eb78ed19e3f94cf3d783330b28fced0a79a2.png";

const HOW_STEPS = [
  { icon: <UserPlus size={22} />, title: "Create Vendor Account", body: "Complete the application in under five minutes." },
  { icon: <Search size={22} />, title: "My Crest Club Reviews", body: "Our team vets every vendor for quality and fit." },
  { icon: <Upload size={22} />, title: "Upload Product Catalog", body: "Add your line sheets, images, and item details." },
  { icon: <Inbox size={22} />, title: "Receive Qualified Requests", body: "Golf shops send you buying requests directly." },
];

const BENEFITS = [
  { icon: <Users size={22} />, title: "Reach Qualified Buyers", body: "Only vetted private clubs and their staff." },
  { icon: <PhoneOff size={22} />, title: "No Cold Calling", body: "Inbound requests, not outbound quotas." },
  { icon: <BookOpen size={22} />, title: "Digital Product Catalog", body: "Line sheets that actually get opened." },
  { icon: <MessageSquare size={22} />, title: "Direct Golf Shop Comms", body: "One thread per order — no lost emails." },
  { icon: <LineChart size={22} />, title: "Sales Analytics", body: "See who's requesting what, and when." },
  { icon: <TrendingUp size={22} />, title: "Business Growth", body: "Concentrated distribution in a discreet market." },
];

const FAQ_ITEMS = [
  {
    q: "Who can become a vendor on My Crest Club?",
    a: "We work with premium apparel, equipment, footwear, headwear, gift, and accessories brands that fit the private-club aesthetic. Our team reviews every application for quality, service history, and fit before approval.",
  },
  {
    q: "How long does the approval process take?",
    a: "Most applications receive a decision within 3–5 business days. You'll be notified by email whether you're approved, so keep an eye on the inbox tied to the application.",
  },
  {
    q: "What does it cost to be a vendor?",
    a: "We offer four tiers — Vendor Access, Professional, Enterprise, and Featured Vendor. Access starts free during our launch period. Detailed pricing is shared with approved vendors during onboarding.",
  },
  {
    q: "Do I need to load my entire catalog to get started?",
    a: "No. Approved vendors can start with a light catalog and expand over time. You control which product categories, territories, and MOQ terms you accept.",
  },
  {
    q: "How do orders and payments work?",
    a: "Golf shops send you qualified buying requests through the platform. You confirm availability, quote, and ship directly. Invoicing runs vendor-to-club through your existing accounts receivable process — My Crest Club is the intake and communication layer, not a payment intermediary.",
  },
  {
    q: "Can I message the golf shop directly?",
    a: "Yes. Every request opens a threaded conversation between your team and the golf shop, so there's a clean record instead of scattered email chains.",
  },
];

export default function VendorsMarketingPage() {
  const [tiers, setTiers] = useState([]);
  const [showVideo, setShowVideo] = useState(false);
  useEffect(() => {
    api.get("/public/vendors/options").then(({ data }) => {
      setTiers(data.pricing_tiers || []);
    }).catch(() => setTiers([]));
  }, []);

  return (
    <MarketingLayout>
      {/* Hero */}
      <section data-testid="vendors-hero" className="relative overflow-hidden">
        <img
          src={SHOP_BG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          aria-hidden
        />
        <div className="absolute inset-0 bg-[#0F2A1C]/78" aria-hidden />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 py-28 sm:py-36 text-[#F9F8F4]">
          <div className="text-[12px] uppercase tracking-[0.32em] font-semibold text-[#D9C68A]">
            Become A Vendor
          </div>
          <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] max-w-4xl">
            Sell to the Nation&rsquo;s Most Exclusive Private Golf Clubs.
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-[#F9F8F4]/90 max-w-3xl leading-relaxed">
            Receive qualified buying requests directly from private golf shops already looking for your products.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/vendors/apply"
              data-testid="vendors-hero-apply"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D9C68A] text-[#1A422D] text-[13px] uppercase tracking-[0.24em] font-semibold hover:bg-[#E5D6A8] transition-colors"
            >
              Become a Vendor <span aria-hidden>→</span>
            </Link>
            <VideoTeaserButton
              poster={marketingVideos.vendors.poster}
              onClick={() => setShowVideo(true)}
              testId="vendors-hero-watch-video"
            />
            <Link
              to="/contact"
              data-testid="vendors-hero-demo"
              className="inline-flex items-center gap-2 px-8 py-4 border border-[#D9C68A] text-[#F9F8F4] text-[13px] uppercase tracking-[0.24em] font-semibold hover:bg-[#D9C68A] hover:text-[#1A422D] transition-colors"
            >
              Schedule Demo
            </Link>
          </div>
        </div>
        <VideoModal
          open={showVideo}
          src={marketingVideos.vendors.src}
          poster={marketingVideos.vendors.poster}
          title={marketingVideos.vendors.title}
          onClose={() => setShowVideo(false)}
        />
      </section>

      {/* How It Works — 4-step timeline */}
      <section data-testid="vendors-how-it-works" className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 py-24">
        <SectionEyebrow>How It Works</SectionEyebrow>
        <h2 className="font-serif text-4xl sm:text-5xl text-[#1A422D] mt-4 max-w-3xl">
          Four steps to being on the desk of every private club in the country.
        </h2>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {HOW_STEPS.map((step, i) => (
            <div
              key={step.title}
              data-testid={`vendors-step-${i + 1}`}
              className="relative p-7 bg-white border border-[#E5E3D8] hover:border-[#B59A5A] transition-colors"
            >
              <div className="absolute -top-4 left-6 px-3 py-1 bg-[#1A422D] text-[#D9C68A] text-[10px] uppercase tracking-[0.28em] font-semibold">
                Step {i + 1}
              </div>
              <div className="mt-3 w-11 h-11 flex items-center justify-center bg-[#1A422D] text-[#D9C68A]">
                {step.icon}
              </div>
              <h3 className="mt-5 font-serif text-2xl text-[#1A422D]">{step.title}</h3>
              <p className="mt-3 text-sm text-[#4A4A4A] leading-relaxed">{step.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section data-testid="vendors-benefits" className="bg-[#F1EFE6] border-y border-[#E5E3D8]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 py-24">
          <SectionEyebrow>Benefits</SectionEyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#1A422D] mt-4 max-w-3xl">
            The advantages of a curated marketplace.
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((b) => (
              <div
                key={b.title}
                data-testid={`vendors-benefit-${b.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="p-7 bg-white border border-[#E5E3D8]"
              >
                <div className="w-11 h-11 flex items-center justify-center bg-[#D9C68A]/25 text-[#8A6A2A]">
                  {b.icon}
                </div>
                <h3 className="mt-5 font-serif text-xl text-[#1A422D]">{b.title}</h3>
                <p className="mt-2 text-sm text-[#4A4A4A] leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing summary — full detail lives on /pricing/vendors */}
      <section data-testid="vendors-pricing" className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 py-24">
        <SectionEyebrow>Pricing</SectionEyebrow>
        <h2 className="font-serif text-4xl sm:text-5xl text-[#1A422D] mt-4 max-w-3xl">
          Plans designed to grow with your private-club business.
        </h2>
        <p className="mt-4 text-[#4A4A4A] max-w-2xl">
          Start with complimentary access to the My Crest Club network, then upgrade as your brand needs greater reach, analytics, support, and visibility.
        </p>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tiers.map((tier) => (
            <PricingCard key={tier.key} tier={tier} />
          ))}
        </div>

        <div className="mt-14 flex flex-wrap justify-center gap-4">
          <Link
            to="/vendors/apply"
            data-testid="vendors-pricing-cta"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#1A422D] text-[#F9F8F4] text-[13px] uppercase tracking-[0.24em] font-semibold hover:bg-[#0F2A1C] transition-colors"
          >
            Start Your Application <span aria-hidden>→</span>
          </Link>
          <Link
            to="/pricing/vendors"
            data-testid="vendors-pricing-see-full"
            className="inline-flex items-center gap-2 px-8 py-4 border border-[#1A422D] text-[#1A422D] text-[13px] uppercase tracking-[0.24em] font-semibold hover:bg-[#1A422D] hover:text-[#F9F8F4] transition-colors"
          >
            See Full Pricing
          </Link>
        </div>
      </section>

      {/* FAQ — Build Guide Phase 3 requirement */}
      <section data-testid="vendors-faq" className="bg-[#F1EFE6] border-y border-[#E5E3D8]">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-14 py-24">
          <SectionEyebrow>Frequently Asked</SectionEyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#1A422D] mt-4 max-w-3xl">
            Answers before you apply.
          </h2>

          <div className="mt-12 divide-y divide-[#E5E3D8] border-t border-b border-[#E5E3D8] bg-white">
            {FAQ_ITEMS.map((item, i) => (
              <FaqRow key={item.q} q={item.q} a={item.a} testId={`vendors-faq-${i + 1}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Become a Vendor — final CTA section per spec */}
      <section data-testid="vendors-become-cta" className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-14 py-24 text-center">
        <SectionEyebrow>Become A Vendor</SectionEyebrow>
        <h2 className="font-serif text-4xl sm:text-5xl text-[#1A422D] mt-4">
          Ready to sell to America&rsquo;s most exclusive clubs?
        </h2>
        <p className="mt-5 text-lg text-[#4A4A4A] max-w-2xl mx-auto">
          Apply in under five minutes. We&rsquo;ll respond within 3&ndash;5 business days.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link
            to="/vendors/apply"
            data-testid="vendors-become-apply"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#1A422D] text-[#F9F8F4] text-[13px] uppercase tracking-[0.24em] font-semibold hover:bg-[#0F2A1C] transition-colors"
          >
            Apply Now <span aria-hidden>→</span>
          </Link>
          <Link
            to="/contact"
            data-testid="vendors-become-demo"
            className="inline-flex items-center gap-2 px-8 py-4 border border-[#1A422D] text-[#1A422D] text-[13px] uppercase tracking-[0.24em] font-semibold hover:bg-[#1A422D] hover:text-[#F9F8F4] transition-colors"
          >
            Schedule Demo
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}

function SectionEyebrow({ children }) {
  return (
    <div className="text-[12px] uppercase tracking-[0.28em] font-semibold text-[#B59A5A]">
      {children}
    </div>
  );
}

function FaqRow({ q, a, testId }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div data-testid={testId} className="px-6 py-5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-4 text-left"
        aria-expanded={open}
      >
        <span className="font-serif text-lg text-[#1A422D]">{q}</span>
        <span className={`text-[#B59A5A] text-2xl leading-none transition-transform ${open ? "rotate-45" : ""}`} aria-hidden>+</span>
      </button>
      {open && <p className="mt-3 text-sm text-[#4A4A4A] leading-relaxed">{a}</p>}
    </div>
  );
}

function PricingCard({ tier }) {
  const featured = tier.featured;
  return (
    <div
      data-testid={`vendors-pricing-tier-${tier.key}`}
      className={`relative p-7 flex flex-col ${
        featured
          ? "bg-[#1A422D] text-[#F9F8F4] border border-[#D9C68A]"
          : "bg-white text-[#1A1A1A] border border-[#E5E3D8]"
      }`}
    >
      {featured && (
        <div className="absolute -top-3 left-6 px-3 py-1 bg-[#D9C68A] text-[#1A422D] text-[10px] uppercase tracking-[0.28em] font-semibold">
          Most Popular
        </div>
      )}
      <div className={`text-[11px] uppercase tracking-[0.24em] font-semibold ${featured ? "text-[#D9C68A]" : "text-[#B59A5A]"}`}>
        Plan
      </div>
      <h3 className={`mt-2 font-serif text-2xl ${featured ? "text-[#F9F8F4]" : "text-[#1A422D]"}`}>
        {tier.name}
      </h3>
      <p className={`mt-2 text-sm leading-relaxed ${featured ? "text-[#F9F8F4]/85" : "text-[#4A4A4A]"}`}>
        {tier.tagline}
      </p>
      <ul className={`mt-5 space-y-2.5 text-sm flex-1 ${featured ? "text-[#F9F8F4]/90" : "text-[#4A4A4A]"}`}>
        {(tier.features || []).map((f) => (
          <li key={f} className="flex gap-2 items-start">
            <Check size={16} className={featured ? "text-[#D9C68A] mt-0.5" : "text-[#1A422D] mt-0.5"} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className={`mt-6 text-[11px] uppercase tracking-[0.24em] font-semibold ${featured ? "text-[#D9C68A]" : "text-[#B59A5A]"}`}>
        Contact for pricing
      </div>
    </div>
  );
}
